import React, { PropsWithChildren } from 'react';
import styles from './ContestLeaderboard.module.scss';
import { Cell, flexRender, getCoreRowModel, Table, useReactTable } from '@tanstack/react-table';
import { BronzeMedalIcon, GoldMedalIcon, SilverMedalIcon } from 'icons/medals';
import { millisecondsToPreciseDuration, PreciseDuration } from 'utils/date';
import {
  leaderboardColumnDefinitions,
  LeaderboardGameType,
  HeaderMetadata,
} from './LeaderboardGameLogic';
import { Leaderboard } from 'utils/contestAPI';
import { Questionmark } from 'components/Icons/Icons';
import { Tooltip } from 'components/Tooltip/Tooltip';
import { Waypoint } from 'react-waypoint';

type PositionCellProps = {
  index: number;
  displayMedal: boolean;
};

const medals = [
  <GoldMedalIcon key="gold" />,
  <SilverMedalIcon key="silver" />,
  <BronzeMedalIcon key="bronze" />,
];

export function PositionCell({ index, displayMedal }: PositionCellProps) {
  const medal = medals[index];

  return (
    <div role="cell" className={styles.indexCell}>
      {displayMedal && <div className={styles.medalWrapper}>{medal}</div>}
      {index + 1}
    </div>
  );
}

export function LeaderboardCell({ cell }: { cell: Cell<LeaderboardContestant, any> }) {
  if (cell.getIsPlaceholder()) {
    return <div />;
  }

  const Component = cell.column.columnDef.cell;

  return Component ? <Component {...cell.getContext()} /> : null;
}

export function useContestLeaderboard(
  gameType: LeaderboardGameType,
  data: Array<LeaderboardContestant>,
  options: { displayMedal: boolean },
) {
  return useReactTable({
    data,
    columns: leaderboardColumnDefinitions(gameType, options),
    getCoreRowModel: getCoreRowModel(),
  });
}

export function ContestLeaderboardGrid({
  table,
  children,
}: PropsWithChildren<{
  table: Table<LeaderboardContestant<any>>;
}>) {
  return (
    <div
      role="table"
      className={styles.grid}
      // used by the CSS to calculate spacing for the columns
      style={{ '--grid-column-count': table.getAllColumns().length }}
    >
      {children}
    </div>
  );
}

export function ContestLeaderboard({ gameType, data, onLoadMore }: ContestLeaderboardGridProps) {
  const table = useContestLeaderboard(gameType, data, {
    displayMedal: true,
  });

  return (
    <div role="table">
      <ContestLeaderboardGrid table={table}>
        {table.getHeaderGroups().flatMap((group) =>
          group.headers.map((header) => {
            const metadata = header.column.columnDef.meta
              ? (header.column.columnDef.meta as HeaderMetadata)
              : null;
            return (
              <div key={header.id} className="small-title" role="columnheader">
                {header.isPlaceholder ? null : (
                  <div className={styles.header}>
                    {flexRender(header.column.columnDef.header, header.getContext())}
                    {metadata?.tooltip && (
                      <Tooltip icon={<Questionmark />} className={styles.headerTooltip}>
                        <p className={styles.tooltipText}> {metadata?.tooltip} </p>
                      </Tooltip>
                    )}
                  </div>
                )}
              </div>
            );
          }),
        )}
        {/* A line spanning from the 2nd row to the end of all the columns */}
        <div style={{ gridArea: `2 / span ${table.getAllColumns().length}` }}>
          <hr className={styles.headerDivider} />
        </div>
        {table.getRowModel().rows.flatMap((row) => {
          return row.getVisibleCells().map((cell) => {
            return (
              <div key={`${row.id}-${cell.id}`}>
                <LeaderboardCell cell={cell} />
              </div>
            );
          });
        })}
      </ContestLeaderboardGrid>
      <Waypoint onEnter={onLoadMore} />
    </div>
  );
}

/**
 * Each game type has its own additional data specific to it
 */
type AdditionalGameLeaderboardData = {
  [LeaderboardGameType.BeastBattler]: {
    healthRemaining: number;
  };
  [LeaderboardGameType.TwilightShift]: {
    time: PreciseDuration;
  };
};

/**
 * Base data associated with every leaderboard
 */
export type LeaderboardContestantBaseData = {
  breakerId: string;
  position: number;
};

export type LeaderboardData<T extends LeaderboardGameType = LeaderboardGameType> = {
  type: T;
  title: string;
  description: string;
  // not sure if this is going to be accessible
  // for all games
  timing?: {
    /**
     * The date the leaderboard event started
     */
    startDate: Date;
    /**
     * The date the leaderboard event ended
     */
    endDate: Date;
  };
  /**
   * Top runs received from the server. This is received sorted server side
   * as the client has no logic for deciding how runs are compared.
   */
  runs: Array<LeaderboardContestant<T>>;
  /**
   * User's best run, if they have a run
   */
  bestRun?: LeaderboardContestant<T>;
};

export type LeaderboardContestant<T extends LeaderboardGameType = LeaderboardGameType> = {
  additionalData: AdditionalGameLeaderboardData[T];
} & LeaderboardContestantBaseData;

export type ContestLeaderboardGridProps = {
  gameType: LeaderboardGameType;
  data: LeaderboardContestant[];
  onLoadMore(): void;
};

export const mapLeaderboardItem = (
  type: LeaderboardGameType,
  item: Leaderboard,
): LeaderboardContestant => {
  switch (type) {
    case LeaderboardGameType.BeastBattler:
      return {
        breakerId: item.Profile?.DisplayName ?? item.PlayFabId,
        additionalData: {
          healthRemaining: item.StatValue,
        },
        position: item.Position,
      } as LeaderboardContestant<LeaderboardGameType.BeastBattler>;
    case LeaderboardGameType.TwilightShift:
      return {
        breakerId: item.Profile?.DisplayName ?? item.PlayFabId,
        additionalData: {
          time: millisecondsToPreciseDuration(item.StatValue * -1),
        },
        position: item.Position,
      } as LeaderboardContestant<LeaderboardGameType.TwilightShift>;
    default:
      throw new Error('Unkown game type');
  }
};
